import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, CheckCircle2, ArrowRight, ChevronDown, ChevronUp, Lock, Server, Globe2, EyeOff } from "lucide-react";
import { SeoHead } from "../../../seo/SeoHead";
import { buildBreadcrumbListJsonLd, buildFAQPageJsonLd } from "../../../seo/site";

const SEO_DESCRIPTION =
  "Sécurité et RGPD Raguia : hébergement OVH France, chiffrement, isolation par entreprise, modèles Mistral EU. Solution IA B2B conforme RGPD pour PME françaises.";

const FAQ = [
  { q: "Raguia est-il conforme au RGPD ?", a: "Oui. Raguia est hébergé sur serveurs OVH en France. Nous utilisons Mistral AI, une société française dont les traitements restent en Union Européenne. Aucune donnée ne transite vers les États-Unis." },
  { q: "Mes documents sont-ils partagés entre entreprises ?", a: "Non. Chaque client dispose d'un espace totalement isolé (architecture multi-tenant). Il est techniquement impossible que les données du Client A soient visibles par le Client B." },
  { q: "Raguia s'entraîne-t-il sur mes données ?", a: "Jamais. Vos documents servent uniquement à répondre à vos questions. Ils ne sont utilisés ni pour entraîner ni pour améliorer nos modèles d'IA." },
  { q: "Que se passe-t-il si je résilie mon abonnement ?", a: "Nous supprimons intégralement vos documents, index vectoriels et données dans les 30 jours suivant la résiliation, conformément au RGPD." },
  { q: "Les données sont-elles chiffrées ?", a: "Oui. Les données sont chiffrées en transit (TLS 1.3) et au repos (AES-256). Des sauvegardes quotidiennes sont réalisées sur serveurs OVH en France." },
];

const PILLARS = [
  { icon: <Globe2 className="size-6 text-[#A43032]" />, title: "Hébergement 100% France", desc: "Serveurs OVH exclusivement sur le territoire français. Souveraineté totale de vos données, sans dérogation ni sous-traitance offshore." },
  { icon: <Lock className="size-6 text-[#A43032]" />, title: "Chiffrement bout en bout", desc: <><span className="font-mono font-bold">TLS 1.3</span> en transit, <span className="font-mono font-bold">AES-256</span> au repos. Vos documents sont illisibles même en cas d'accès physique aux serveurs.</> },
  { icon: <EyeOff className="size-6 text-[#A43032]" />, title: "Zéro entraînement IA", desc: "Vos documents servent uniquement à répondre à vos questions. Jamais utilisés pour améliorer des modèles tiers." },
  { icon: <Server className="size-6 text-[#A43032]" />, title: "Isolation stricte par client", desc: "Architecture multi-tenant isolée. Chaque entreprise est cloisonnée. Le Service RH ne voit que ses documents, jamais ceux de la comptabilité." },
  { icon: <Shield className="size-6 text-[#A43032]" />, title: "IA européenne (Mistral AI)", desc: "Nous utilisons les modèles de Mistral AI, entreprise française. Tous les traitements IA restent en Union Européenne, sans dépendance américaine." },
  { icon: <CheckCircle2 className="size-6 text-[#A43032]" />, title: "Suppression garantie", desc: "Résiliez à tout moment. Vos données, documents et index sont supprimés intégralement dans les 30 jours, conformément au RGPD." },
];

export default function SecuritePage() {
  const [open, setOpen] = useState(null);

  return (
    <>
      <SeoHead
        title="Sécurité & RGPD — Raguia | IA documentaire hébergée en France"
        description={SEO_DESCRIPTION}
        jsonLd={[
          buildBreadcrumbListJsonLd([
            { name: "Accueil", path: "/" },
            { name: "Fonctionnalités", path: "/fonctionnalites" },
            { name: "Sécurité RGPD", path: "/fonctionnalites/securite" },
          ]),
          buildFAQPageJsonLd(FAQ),
        ]}
      />
      <main className="min-h-screen bg-stone-50">
        {/* HERO */}
        <section className="relative px-4 pt-16 pb-12 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden">
          <div className="absolute top-20 left-10 size-72 bg-[#A43032]/5 rounded-full blur-3xl" />
          <div className="absolute top-40 right-10 size-96 bg-[#A43032]/8 rounded-full blur-3xl" />
          <div className="relative max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#A43032]/30 bg-[#A43032]/10 px-4 py-2 text-sm font-semibold text-[#A43032] mb-8">
              <Shield className="size-4" /> 🇫🇷 Sécurité & RGPD
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-[#010101] leading-[1.1] mb-6">
              Vos documents confidentiels,{" "}
              <span className="text-[#A43032]">sécurisés en France</span>
            </h1>
            <p className="text-lg md:text-xl text-stone-600 max-w-2xl font-medium mb-4">
              Hébergement OVH France, chiffrement, isolation stricte par client, IA Mistral européenne. Raguia est conçu pour les entreprises qui ne peuvent pas se permettre de prendre des risques avec leurs données.
            </p>
            <p className="sr-only">
              Solution IA RGPD B2B hébergée en France. Chatbot documentaire conforme RGPD pour PME, avocats, cabinets comptables. Pas de données aux États-Unis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#A43032] px-8 py-4 text-base font-bold text-white shadow-xl hover:scale-105 hover:bg-[#8a2729] transition-all">
                Demander une démo sécurisée <ArrowRight className="size-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* PILIERS SÉCURITÉ */}
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-7xl mx-auto border-t border-stone-200">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#010101] mb-10 text-center">
            6 piliers de sécurité
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl border-2 border-stone-200 p-6 shadow-sm hover:border-[#A43032]/30 hover:shadow-md transition-all group">
                <div className="size-12 rounded-xl bg-[#A43032]/10 flex items-center justify-center mb-4 group-hover:bg-[#A43032] transition-colors">
                  <div className="group-hover:text-white transition-colors">{p.icon}</div>
                </div>
                <h3 className="font-heading font-semibold text-[#010101] mb-2 text-base">{p.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* COMPARATIF CHATGPT */}
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-[#010101] to-stone-900 p-8 md:p-12 text-white border border-stone-800 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(164,48,50,0.2),transparent_60%)]" />
            <div className="relative z-10">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-8 text-center">
                Pourquoi pas ChatGPT pour vos documents ?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-red-950/30 border border-red-500/30 rounded-2xl p-6">
                  <h3 className="font-heading font-semibold text-red-400 mb-4">ChatGPT (OpenAI)</h3>
                  <ul className="space-y-3 text-stone-300 text-sm">
                    {["Données hébergées aux États-Unis", "Risque de réentraînement des modèles", "Pas d'isolation par entreprise", "Conformité RGPD incertaine", "Répond sur ses données d'entraînement, pas les vôtres"].map((r) => (
                      <li key={r} className="flex items-start gap-2"><span className="text-red-400 font-bold shrink-0">✗</span>{r}</li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#A43032]/10 border border-[#A43032]/30 rounded-2xl p-6">
                  <h3 className="font-heading font-semibold text-[#A43032] mb-4">Raguia</h3>
                  <ul className="space-y-3 text-stone-200 text-sm">
                    {["Hébergé chez OVH en France 🇫🇷", "Aucun entraînement sur vos données", "Isolation stricte par client", "Conforme RGPD, IA Mistral EU", "Répond uniquement depuis VOS documents"].map((r, i) => (
                      <li key={i} className="flex items-start gap-2"><CheckCircle2 className="size-4 text-[#A43032] shrink-0 mt-0.5" />{r}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 py-16 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl font-semibold text-[#010101] mb-8 text-center">Questions sur la sécurité</h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border-2 border-stone-200 overflow-hidden hover:border-[#A43032]/30 transition-all shadow-sm">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left font-bold text-[#010101] hover:bg-stone-50 transition-colors">
                  <span>{item.q}</span>
                  {open === i ? <ChevronUp className="size-5 text-[#A43032] shrink-0" /> : <ChevronDown className="size-5 text-stone-400 shrink-0" />}
                </button>
                {open === i && <div className="px-5 pb-5 border-t border-stone-100"><p className="pt-4 text-stone-600 font-medium leading-relaxed">{item.a}</p></div>}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 pb-20 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#A43032] to-[#8a2729] rounded-3xl p-10 text-white text-center shadow-2xl">
            <h2 className="font-heading text-3xl font-semibold mb-4">Vos données méritent la sécurité maximale</h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">Démo gratuite sur vos propres documents. Hébergement France, sans engagement.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-[#A43032] transition hover:scale-105">
              Demander une démo <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
